import { ChevronDownIcon, DownloadIcon } from "@chakra-ui/icons";
import {
  Box,
  Card,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Progress,
  TableContainer,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";
import {
  DocumentReference as FHIRDocumentReference,
  Organization as FhirOrganization,
  Practitioner as FhirPractitioner,
} from "@medplum/fhirtypes";
import { DocumentQuery } from "@metriport/api-sdk";
import { useCallback, useEffect, useState } from "react";
import { useInterval } from "../../hooks/use-interval";
import { capture } from "../../shared/capture";
import { Actions, Analytics, Features } from "../shared/analytics";
import { Button } from "../shared/Button";
import { formatDate } from "../shared/date";
import { DateRange } from "../shared/form/DateRange";
import { Input } from "../shared/form/Input";
import Table from "../shared/Table";
import useMetriportToast from "../shared/toast";
import { MetriportMedicalApiProps } from "./shared";
import { bytesToSize } from "../../shared/util";

const NA = "--";
const POLLING_INTERVAL = 3_000; //ms
const docRefDateRangeId = "doc-ref-date-range";
const docRefContentId = "doc-ref-content";

export type PatientRecordsProps = MetriportMedicalApiProps &
  Pick<DrawerProps, "placement" | "size" | "onClose"> &
  Partial<Pick<DrawerProps, "isOpen">> & {
    patientId: string;
    facilityId: string;
  };

type QueryStatus = {
  isQuerying: boolean;
  queryProgress?: {
    total: number;
    completed: number;
  };
};

function isXml(mimeType: string | undefined): boolean {
  return mimeType ? mimeType.includes("xml") : false;
}

export default function patientDocuments({
  api,
  patientId,
  facilityId,
  ...props
}: PatientRecordsProps) {
  const toast = useMetriportToast();
  const [isLoadingRecords, setIsLoadingRecords] = useState<boolean>(true);
  const [docs, setDocs] = useState<FHIRDocumentReference[]>([]);
  const [isDownloading, setIsDownloading] = useState<Record<string, boolean>>({});
  const [queryStatus, setQueryStatus] = useState<QueryStatus>({
    isQuerying: false,
  });

  const updateQueryingStatus = useCallback((docQuery: DocumentQuery) => {
    const successful = docQuery.download?.successful ?? 0;
    const errors = docQuery.download?.errors ?? 0;

    return setQueryStatus({
      isQuerying: docQuery.download?.status === "processing",
      queryProgress: {
        total: docQuery.download?.total ? docQuery.download.total : 0,
        completed: successful + errors,
      },
    });
  }, []);

  async function sendDocQuery(): Promise<void> {
    const docQuery = await api.startDocumentQuery(patientId, facilityId);
    updateQueryingStatus(docQuery);
  }

  useInterval(
    async () => {
      const { download } = await api.getDocumentQueryStatus(patientId);
      updateQueryingStatus({ download });
      if (download?.status === "completed") {
        const documentList = await api.listDocuments(patientId);

        setDocs(documentList.documents);
      }
    },
    queryStatus.isQuerying ? POLLING_INTERVAL : null
  );

  async function onGetRecords({
    dateFrom,
    dateTo,
    content,
  }: {
    dateFrom?: string;
    dateTo?: string;
    content?: string;
  }): Promise<void> {
    setIsLoadingRecords(true);

    localStorage.setItem("filters", JSON.stringify({ dateFrom, dateTo, content }));

    try {
      const dateFromValue = getFilterValue(dateFrom);
      const dateToValue = getFilterValue(dateTo);
      const contentValue = getFilterValue(content);

      const documentList = await api.listDocuments(patientId, {
        dateFrom: dateFromValue,
        dateTo: dateToValue,
        content: contentValue,
      });

      setDocs(documentList.documents);
      if (!documentList.documents.length) {
        await sendDocQuery();
        return;
      }
      const { download } = await api.getDocumentQueryStatus(patientId);
      updateQueryingStatus({ download });
    } catch (err) {
      capture.error(err, { extra: { patientId, facilityId, context: `patient.file.list` } });
      toast.error();
    } finally {
      setIsLoadingRecords(false);
    }
  }

  function getFilterValue(filterInput: string | undefined): string | undefined {
    if (!filterInput) return;
    const trimmedValue = filterInput.trim();
    return trimmedValue && trimmedValue.length > 0 ? trimmedValue : undefined;
  }

  useEffect(() => {
    const filters = localStorage.getItem("filters");
    if (filters) {
      const { dateFrom, dateTo, content } = JSON.parse(filters);
      onGetRecords({ dateFrom, dateTo, content });
    } else {
      onGetRecords({});
    }
  }, [patientId]);

  async function onDownloadFile(
    docId: string,
    docFilename: string,
    conversionType?: "html" | "pdf"
  ): Promise<void> {
    Analytics.emit(Actions.download, Features.document);
    setIsDownloading({
      ...isDownloading,
      [docId]: true,
    });
    try {
      if (conversionType) {
        toast.info({
          title: "Hold tight... This might take up to 30 seconds.",
          duration: 5000,
        });
      }
      const resp = await api.getDocumentUrl(docFilename, conversionType);

      const a = document.createElement("a");
      a.href = resp.url;
      a.download = docFilename;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      capture.error(err, { extra: { docId: docId, context: `patient.file.download` } });
      toast.error();
    }
    setIsDownloading({
      ...isDownloading,
      [docId]: false,
    });
  }

  return (
    <Drawer {...props} isOpen={props.isOpen !== undefined ? props.isOpen : true} size={"full"}>
      <DrawerOverlay />
      <DrawerContent
        // https://github.com/chakra-ui/chakra-ui/issues/7091
        motionProps={{ initial: "none", exit: "none" }}
      >
        <DrawerCloseButton />
        <DrawerHeader>Medical Documents</DrawerHeader>
        <DrawerBody position={"relative"}>
          <Filters onGetRecords={onGetRecords} />
          <TableContainer overscrollBehaviorX={"none"}>
            <Table
              variant="striped"
              columns={[
                "Description",
                "Size",
                "Content Type",
                "Organization",
                "Practitioner",
                "Date Created",
                "",
              ]}
              isLoading={isLoadingRecords}
              skeletonHeight={"30px"}
            >
              {docs
                .sort((a, b) => {
                  if (a.date && b.date) {
                    return new Date(b.date).getTime() - new Date(a.date).getTime();
                  }
                  return 0;
                })
                .map((doc, idx) => (
                  <Tr key={idx}>
                    <Td className="ph-no-capture">
                      <Text isTruncated>{doc.description ?? NA}</Text>
                    </Td>
                    <Td className="ph-no-capture">
                      {doc.content?.[0].attachment?.size
                        ? bytesToSize(doc.content[0].attachment.size)
                        : NA}
                    </Td>
                    <Td className="ph-no-capture">
                      {doc.content?.[0].attachment?.contentType ?? NA}
                    </Td>
                    <Td className="ph-no-capture">{getOrganizationName(doc)}</Td>
                    <Td className="ph-no-capture">{getPractitionerName(doc)}</Td>
                    <Td className="ph-no-capture">{doc.date ? formatDate(doc.date) : NA}</Td>
                    <Td>
                      <ActionsRowContent
                        doc={doc}
                        onDownloadFile={onDownloadFile}
                        isDownloading={isDownloading}
                      />
                    </Td>
                  </Tr>
                ))}
            </Table>
          </TableContainer>
          <ProgressContainer queryStatus={queryStatus} />
        </DrawerBody>
        <DrawerFooter borderTopWidth="1px">
          <Button mr={3} isLoading={queryStatus.isQuerying} onClick={sendDocQuery}>
            Query Documents
          </Button>
          <Button mr={3} onClick={props.onClose}>
            Close
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

const getOrganizationName = (doc: FHIRDocumentReference) => {
  if (doc.contained) {
    const org = doc.contained.find(c => c.resourceType === "Organization") as FhirOrganization;

    if (!org) return NA;

    return org.name;
  }
};

const getPractitionerName = (doc: FHIRDocumentReference) => {
  if (doc.contained) {
    const practitioner = doc.contained.find(
      c => c.resourceType === "Practitioner"
    ) as FhirPractitioner;

    if (!practitioner) return NA;

    return practitioner.name?.[0].given?.[0] + " " + practitioner.name?.[0].family;
  }
};

const Filters = ({
  onGetRecords,
}: {
  onGetRecords: ({
    dateFrom,
    dateTo,
    content,
  }: {
    dateFrom?: string;
    dateTo?: string;
    content?: string;
  }) => Promise<void>;
}) => {
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [content, setContent] = useState<string>("");

  useEffect(() => {
    const filters = localStorage.getItem("filters");
    if (filters) {
      const { dateFrom, dateTo, content } = JSON.parse(filters);
      setDateFrom(dateFrom);
      setDateTo(dateTo);
      setContent(content);
    }
  }, []);

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setContent("");
    onGetRecords({});
  };

  return (
    <HStack mb={5} alignItems={"end"}>
      <DateRange
        id={docRefDateRangeId}
        isRequired={false}
        label="Filter by date"
        dateFrom={dateFrom}
        dateTo={dateTo}
        onSetToDate={(to: string) => setDateTo(to)}
        onSetFromDate={(from: string) => setDateFrom(from)}
      />
      <Input
        id={docRefContentId}
        value={content}
        isRequired={false}
        label="Filter by content"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContent(e.target.value)}
      />
      <Button onClick={() => onGetRecords({ dateFrom, dateTo, content })}>Search</Button>
      <Button
        style={{ backgroundColor: "transparent", borderColor: "#748df0", color: "#748df0" }}
        variant="outline"
        onClick={clearFilters}
      >
        Clear
      </Button>
    </HStack>
  );
};

const ActionsRowContent = ({
  doc,
  isDownloading,
  onDownloadFile,
}: {
  doc: FHIRDocumentReference;
  isDownloading: Record<string, boolean>;
  onDownloadFile: (
    docId: string,
    docFilename: string,
    conversionType?: "html" | "pdf"
  ) => Promise<void>;
}) => {
  const docId = doc.id;
  const docContentAttachment = doc.content?.[0].attachment;

  if (docId && docContentAttachment && docContentAttachment.title) {
    const contentType = docContentAttachment.contentType;
    const fileName = docContentAttachment.title;

    if (isXml(contentType)) {
      return (
        <Menu>
          <MenuButton>
            <Button isLoading={isDownloading[docId] ?? false}>
              Save As...
              <ChevronDownIcon />
            </Button>
          </MenuButton>
          <MenuList>
            <MenuItem minH="40px">
              <span onClick={() => onDownloadFile(docId, fileName, "html")}>Converted HTML</span>
            </MenuItem>
            <MenuItem minH="40px">
              <span onClick={() => onDownloadFile(docId, fileName, "pdf")}>Converted PDF</span>
            </MenuItem>
            <MenuItem minH="40px">
              <span onClick={() => onDownloadFile(docId, fileName)}>Raw C-CDA XML</span>
            </MenuItem>
          </MenuList>
        </Menu>
      );
    }

    return (
      <Button
        onClick={() => onDownloadFile(docId, fileName)}
        rightIcon={<DownloadIcon />}
        isLoading={isDownloading[docId] ?? false}
      >
        Save
      </Button>
    );
  }

  return null;
};

const ProgressContainer = ({ queryStatus }: { queryStatus: QueryStatus }) => {
  const progressValue = queryStatus.queryProgress?.total
    ? (queryStatus.queryProgress.completed / queryStatus.queryProgress.total) * 100
    : 0;

  if (queryStatus.isQuerying) {
    return (
      <Box
        w={"100%"}
        position="fixed"
        left={0}
        right={0}
        bottom={"100px"}
        display="flex"
        justifyContent="center"
      >
        <Card border={"solid #d3d3d3 1px"} p={5} w={500} mx={5}>
          <Flex mb={2} justifyContent={"space-between"}>
            <Text>Querying documents</Text>
            {queryStatus.queryProgress && (
              <Text>
                {queryStatus.queryProgress.completed} / {queryStatus.queryProgress.total}
              </Text>
            )}
          </Flex>
          <Progress colorScheme={"purple"} hasStripe value={progressValue} />
        </Card>
      </Box>
    );
  }

  return null;
};
